// What the assistant offers to do on the invoice on screen, worked out by the
// back when it sends the invoice (metadata.suggested_actions, already sorted).
export interface SuggestedAction {
  key: string
  // ai: run by the assistant in the chat; direct: run here, without the assistant
  kind: 'ai' | 'direct'
  // Already translated by the back (X-Locale), the reminder step included
  label: string
  params: Record<string, any>
  priority: number
}

export interface SuggestedActionResource {
  type: 'invoice'
  id: string | number
}

// One line of the bubble: the back's label, or a front translation key with
// params that are translation keys themselves (one line per conversion target).
export interface SuggestedActionEntry {
  id: string
  action: SuggestedAction
  icon: string
  label: { text: string } | { key: string; params: Record<string, string> }
  docType?: string
}

// Direct actions run by the invoice view that owns their UI (a modal, a menu),
// through aiAssistant/requestSuggestedAction.
export const VIEW_HANDLED_ACTIONS = [
  'establish',
  'convert_document',
  'log_phone_follow_up',
  'link_payments'
]

const SUGGESTED_ACTION_ICONS: Record<string, string> = {
  send_reminder: 'bell',
  send_by_email: 'envelope',
  send_quote_follow_up: 'envelope',
  send_payment_confirmation: 'envelope-circle-check',
  explain_chift_error: 'circle-exclamation',
  explain_peppol_rejection: 'circle-exclamation',
  resend_bounced_email: 'envelope-open',
  send_to_peppol: 'paper-plane',
  check_peppol_registration: 'magnifying-glass',
  establish: 'check',
  convert_document: 'arrow-right',
  log_phone_follow_up: 'phone',
  complete_recipient_email: 'at',
  link_payments: 'piggy-bank',
  add_dossier_items: 'boxes-stacked'
}

/**
 * The bubble's lines, in the order the back sent the actions. A key this front
 * does not know yet (the back shipped first) is left out.
 */
export function suggestedActionEntries(
  actions: SuggestedAction[]
): SuggestedActionEntry[] {
  return (actions ?? [])
    .filter((action) =>
      Object.prototype.hasOwnProperty.call(SUGGESTED_ACTION_ICONS, action.key)
    )
    .flatMap((action) => {
      const icon = SUGGESTED_ACTION_ICONS[action.key]
      // The back's label is generic: name the target type instead
      if (action.key === 'convert_document') {
        return (action.params?.doc_types ?? []).map((docType: string) => ({
          id: `${action.key}.${docType}`,
          action,
          icon,
          docType,
          label: {
            key: 'button.convert_to',
            params: { var: `enums.invoicing.docTypes.${docType}` }
          }
        }))
      }
      return [{ id: action.key, action, icon, label: { text: action.label } }]
    })
}

// The recipient's page, where their email can be filled in
export function recipientRoute(type: string, id: string | number) {
  switch (type?.split('\\').pop()?.toLowerCase()) {
    case 'person':
      return { name: 'person_view_route', params: { personId: id } }
    case 'contact':
      return { name: 'contact_view_route', params: { contactId: id } }
    case 'company':
      return { name: 'settings_edit_company_route', params: { id } }
    case 'supplier':
      return {
        name: 'settings_edit_supplier_route',
        params: { supplierId: id }
      }
    default:
      return null
  }
}
